import { documentTypeExtractionFieldApi } from 'app/services/api';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import { catchAsyncActionError } from 'utils/action';

import { getDocumentExtractionFields } from 'store/system/selectors';

export const GET_DOCUMENT_EXTRACTION_FIELDS_TYPE = 'GET_DOCUMENT_EXTRACTION_FIELDS_TYPE';
export const ADD_DOCUMENT_EXTRACTION_FIELDS_TYPE = 'ADD_DOCUMENT_EXTRACTION_FIELDS_TYPE';
export const EDIT_DOCUMENT_EXTRACTION_FIELDS_TYPE = 'EDIT_DOCUMENT_EXTRACTION_FIELDS_TYPE';
export const DELETE_DOCUMENT_EXTRACTION_FIELDS_TYPE = 'DELETE_DOCUMENT_EXTRACTION_FIELDS_TYPE';
export const GET_DOCUMENT_EXTRACTION_FIELDS_TYPE_BY_ID =
  'GET_DOCUMENT_EXTRACTION_FIELDS_TYPE_BY_ID';
export const GET_INSPECTOR_EXTRACTION_FIELDS_BY_TYPE = 'GET_INSPECTOR_EXTRACTION_FIELDS_BY_TYPE';

const documentExtractionFields = new schema.Entity(
  'documentExtractionFields',
  {},
  {
    idAttribute: 'id',
    // Must not mutate: the local-dev RequestDeduplicator can hand this the SAME response
    // objects another consumer already stored through an immer reducer (deep-frozen in dev),
    // so an in-place write throws "object is not extensible" and the fields never load.
    processStrategy: entity => ({
      ...entity,
      documentFieldTypeId: entity.documentFieldType.id,
    }),
  },
);

export const getDocumentExtractionFieldsAction = catchAsyncActionError(
  ({ documentTypeId }) =>
    async dispatch => {
      const { data } =
        await documentTypeExtractionFieldApi.getDocumentTypeExtractionFieldByDocumentTypeId({
          typeId: documentTypeId,
        });

      return dispatch({
        type: GET_DOCUMENT_EXTRACTION_FIELDS_TYPE,
        payload: normalize(data, [documentExtractionFields]).entities,
      });
    },
);

export const getDocumentExtractionFieldsByExtractionFieldIdAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } =
      await documentTypeExtractionFieldApi.getDocumentTypeExtractionFieldByDocumentTypeExtractionFieldId(
        params,
      );

    return dispatch({
      type: GET_DOCUMENT_EXTRACTION_FIELDS_TYPE_BY_ID,
      payload: normalize([data], [documentExtractionFields]).entities,
    });
  },
);

export const addDocumentExtractionFieldsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentTypeExtractionFieldApi.createDocumentTypeExtractionField(params);

  // Carry the submitted document-tag (discriminator) flag onto the stored entity so the
  // last-set-wins clearing reflects optimistically even before the backend echoes it back.
  data.isDiscriminator = params.isDiscriminator;

  return dispatch({
    type: ADD_DOCUMENT_EXTRACTION_FIELDS_TYPE,
    payload: normalize([data], [documentExtractionFields]).entities,
  });
});

export const editDocumentExtractionFieldsAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await documentTypeExtractionFieldApi.editDocumentTypeExtractionField(params);

    data.documentFieldTypeId = params.documentFieldTypeId;
    data.isDiscriminator = params.isDiscriminator;

    return dispatch({
      type: EDIT_DOCUMENT_EXTRACTION_FIELDS_TYPE,
      payload: data,
    });
  },
);

export const deleteDocumentExtractionFieldsAction = catchAsyncActionError(
  ({ extractionFieldId, typeId }) =>
    async dispatch => {
      await documentTypeExtractionFieldApi.deleteDocumentTypeExtractionField({
        extractionFieldId,
        typeId,
      });

      return dispatch({
        type: DELETE_DOCUMENT_EXTRACTION_FIELDS_TYPE,
        payload: extractionFieldId,
      });
    },
);

export const reorderDocumentExtractionFieldsLocallyAction = params => (dispatch, getState) => {
  const state = getState();
  const documentExtractionFields = getDocumentExtractionFields(state);

  const clonedDocumentExtractionFields = _.cloneDeep(documentExtractionFields);

  params.forEach((item, index) => {
    clonedDocumentExtractionFields[item.id].orderNumber = index;
  });

  return dispatch({
    type: GET_DOCUMENT_EXTRACTION_FIELDS_TYPE,
    payload: {
      documentExtractionFields: clonedDocumentExtractionFields,
    },
  });
};

export const reorderDocumentExtractionFieldsAction = catchAsyncActionError(
  body => async dispatch => {
    dispatch(reorderDocumentExtractionFieldsLocallyAction(body.extractionFieldOrderNumbers));

    await documentTypeExtractionFieldApi.reorderDocumentTypeExtractionField(body);
  },
);

/**
 * Fetches the flat list of extraction field definitions for a document type and stores them
 * keyed by documentTypeId in the inspector slice. Used by DocumentExtractionFieldsTab as a
 * fallback when the document was loaded without embedded definitions (most doc types via
 * getDocumentsV2 do not embed ExtractionFields in the document payload).
 *
 * Unlike getDocumentExtractionFieldsAction (which overwrites the shared system flat map),
 * this stores the raw flat array per-typeId and does not touch the normalizr entity map.
 *
 * @param {{ documentTypeId: string }} params
 */
export const getInspectorExtractionFieldsByDocTypeIdAction = catchAsyncActionError(
  ({ documentTypeId }) =>
    async dispatch => {
      const { data } =
        await documentTypeExtractionFieldApi.getDocumentTypeExtractionFieldByDocumentTypeId({
          typeId: documentTypeId,
        });

      return dispatch({
        type: GET_INSPECTOR_EXTRACTION_FIELDS_BY_TYPE,
        payload: { documentTypeId, fields: data },
      });
    },
);
